<template>
  <div class="auth-page container py-5">
    <div class="row justify-content-center">
      <div class="col-md-6 col-lg-5">
        <div class="card auth-card">
          <h2>{{ t.title }}</h2>
          <p class="muted">{{ t.subtitle }}</p>

          <div v-if="!email || !token" class="error-box">{{ t.invalidLink }}</div>

          <div v-else-if="success" class="success-box">
            <p>{{ t.success }}</p>
            <router-link :to="{ name: 'login' }" class="login-btn">{{ t.goLogin }}</router-link>
          </div>

          <form v-else @submit.prevent="onSubmit">
            <label class="field">
              <span>{{ t.email }}</span>
              <input type="email" :value="email" disabled />
            </label>
            <label class="field">
              <span>{{ t.newPassword }}</span>
              <input type="password" v-model="newPassword" required minlength="6" />
            </label>
            <label class="field">
              <span>{{ t.confirm }}</span>
              <input type="password" v-model="confirmPassword" required minlength="6" />
            </label>

            <button type="submit" class="primary" :disabled="busy">
              {{ busy ? t.submitting : t.submit }}
            </button>
          </form>

          <div v-if="!success" class="alt">
            <router-link :to="{ name: 'login' }">{{ t.backToLogin }}</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const I18N = {
  az: {
    title: "Yeni şifrə təyin et",
    subtitle: "Aşağıda yeni şifrəni daxil edin.",
    email: "Email",
    newPassword: "Yeni şifrə",
    confirm: "Şifrəni təsdiqlə",
    submit: "Şifrəni yenilə",
    submitting: "Yenilənir…",
    success: "Şifrə uğurla yeniləndi! İndi yeni şifrə ilə daxil ola bilərsiniz.",
    goLogin: "Daxil ol",
    backToLogin: "← Daxil olma səhifəsi",
    invalidLink: "Link etibarsızdır və ya köhnəlib. Yenidən bərpa sorğusu göndərin.",
    mismatch: "Şifrələr uyğun gəlmir.",
  },
  de: {
    title: "Neues Passwort festlegen",
    subtitle: "Geben Sie Ihr neues Passwort unten ein.",
    email: "E-Mail",
    newPassword: "Neues Passwort",
    confirm: "Passwort bestätigen",
    submit: "Passwort aktualisieren",
    submitting: "Wird aktualisiert…",
    success: "Passwort erfolgreich aktualisiert! Sie können sich jetzt mit dem neuen Passwort anmelden.",
    goLogin: "Anmelden",
    backToLogin: "← Zurück zur Anmeldung",
    invalidLink: "Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.",
    mismatch: "Die Passwörter stimmen nicht überein.",
  },
  en: {
    title: "Set a new password",
    subtitle: "Enter your new password below.",
    email: "Email",
    newPassword: "New password",
    confirm: "Confirm password",
    submit: "Update password",
    submitting: "Updating…",
    success: "Password updated! You can now sign in with your new password.",
    goLogin: "Sign in",
    backToLogin: "← Back to sign in",
    invalidLink: "The link is invalid or expired. Please request a new reset.",
    mismatch: "Passwords don't match.",
  },
  ru: {
    title: "Установите новый пароль",
    subtitle: "Введите ваш новый пароль ниже.",
    email: "Email",
    newPassword: "Новый пароль",
    confirm: "Подтвердите пароль",
    submit: "Обновить пароль",
    submitting: "Обновление…",
    success: "Пароль успешно обновлён! Теперь вы можете войти с новым паролем.",
    goLogin: "Войти",
    backToLogin: "← Назад к входу",
    invalidLink: "Ссылка недействительна или просрочена. Запросите новую.",
    mismatch: "Пароли не совпадают.",
  },
  tr: {
    title: "Yeni şifre belirle",
    subtitle: "Yeni şifrenizi aşağıya girin.",
    email: "E-posta",
    newPassword: "Yeni şifre",
    confirm: "Şifreyi onayla",
    submit: "Şifreyi güncelle",
    submitting: "Güncelleniyor…",
    success: "Şifre güncellendi! Artık yeni şifrenizle giriş yapabilirsiniz.",
    goLogin: "Giriş yap",
    backToLogin: "← Girişe dön",
    invalidLink: "Bağlantı geçersiz veya süresi dolmuş. Yeni bir sıfırlama isteği gönderin.",
    mismatch: "Şifreler eşleşmiyor.",
  },
};

export default {
  name: "ResetPasswordPage",
  data() {
    return { newPassword: "", confirmPassword: "", busy: false, success: false };
  },
  computed: {
    t() { return I18N[this.$store.state.lang] || I18N.az; },
    email() { return this.$route.query.email || ""; },
    token() { return this.$route.query.token || ""; },
  },
  methods: {
    async onSubmit() {
      if (this.newPassword !== this.confirmPassword) {
        this.$toast.error(this.t.mismatch);
        return;
      }
      this.busy = true;
      try {
        await axios.post(`/identity/reset-password`, {
          email: this.email,
          token: this.token,
          newPassword: this.newPassword,
        });
        this.success = true;
      } catch (e) {
        // toast shown by interceptor
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-page { min-height: 60vh; }
.auth-card {
  background: #fff; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 14px rgba(0,0,0,0.06);
  h2 { color: #1e3c72; margin-bottom: 0.25rem; }
  .muted { color: #64748b; margin-bottom: 1.5rem; font-size: 0.9rem; }
  .field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 1rem;
    span { font-size: 0.78rem; color: #475569; font-weight: 600; }
    input { padding: 0.6rem 0.8rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none;
      &:focus { border-color: #2a5298; }
      &:disabled { background: #f7f9fc; color: #64748b; }
    }
  }
  button.primary { width: 100%; padding: 0.7rem; background: #2a5298; color: #fff; border: 0; border-radius: 6px; cursor: pointer; font: inherit; font-weight: 600;
    &:hover:not(:disabled) { background: #1e3c72; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  .success-box { background: #e6f6ed; color: #1e7d44; padding: 1.25rem; border-radius: 8px; border: 1px solid #b6e5c8; font-size: 0.9rem; text-align: center;
    p { margin-bottom: 1rem; }
    .login-btn { display: inline-block; padding: 0.55rem 1.5rem; background: #2a5298; color: #fff; text-decoration: none; border-radius: 6px; font-weight: 600;
      &:hover { background: #1e3c72; }
    }
  }
  .error-box { background: #fdecea; color: #c0392b; padding: 1rem; border-radius: 8px; border: 1px solid #f1c2bd; font-size: 0.9rem; }
  .alt { margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid #eef0f4; text-align: center; font-size: 0.85rem;
    a { color: #2a5298; text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
